import React, { Fragment } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import SmartRateModal from './SmartRateModal'
import FadeInUpWrapper from '../common/FadeInUpWrapper'
import Modal from '../common/Modal'
import PermutationDots from './helpers/PermutationDots'
import AlertTriangle from '../common/icons/AlertTriangle'
import truncateFloat from '../common/helpers/truncateFloat'

import Spinner from '../common/Spinner'

const getMargin = parentComponent => {
  switch (parentComponent) {
    case 'pipeline':
      return 0
    case 'loanStatusHeader':
      return 20
    case 'loansList':
      return 27
    default:
      return 0
  }
}

const Container = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  color: ${(props) => (props.color === 'blue' ? '#2e8ab8' : '#56565a')};
  text-align: ${props => props.centerText ? 'center' : 'left'};
  border-bottom: 1px solid transparent;
  ::hover {
    border-bottom: 1px solid ${props => (props.color === 'blue' ? '#2e8ab8' : '#56565a')};
  }
`
const Divider = styled.div`
  border-right: 1px solid ${props => (props.color === 'blue' ? '#2e8ab8' : '#56565a')};
  height: 13px;
`
const DotContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 3px;
`
const Spacer = styled.div`
  height: 12px;
  width: ${props => props.percentVisible ? 6 : 10}px;
`
const TextAlign = styled.div`
  width: 100%;
  text-align: ${props => props.centerText ? 'center' : 'left'};
`
const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`
const ErrorAlert = styled(AlertTriangle)`
  margin-left: ${props => props.parentComponent === 'pipeline' ? 2 : 0}px;
`

export class SmartRateDisplay extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isSmartRateModalOpen: false
    }
  }
  toggleSmartRateModal = () => {
    const { isSmartRateModalOpen } = this.state
    this.setState({ isSmartRateModalOpen: !isSmartRateModalOpen })
  }
  toggleModal = () => {
    const { toggleDraggable } = this.props
    if (toggleDraggable) {
      toggleDraggable() // Disable drag if we are in pipeline and the modal is opened, or enable if the modal is being closed
    }
    this.toggleSmartRateModal()
  }
  render() {
    const {
      color,
      smartRate,
      businessName,
      dataPermutation,
      defaultProbability,
      needsRedecision,
      productUsesDefaultModel,
      parentComponent,
      loanId,
      error,
      loading,
      centerText
    } = this.props
    const { isSmartRateModalOpen } = this.state
    const showPercent = productUsesDefaultModel && defaultProbability && !needsRedecision
    const percentText = showPercent ? `${truncateFloat({ float: defaultProbability * 100, n: 1, truncateZeroes: true })}%` : '?'
    const isNotPipeline = parentComponent !== 'pipeline'
    if (loading) {
      return (
        <SpinnerContainer>
          <Spinner height={18} width={18} />
        </SpinnerContainer>
      )
    } if (error && !smartRate) {
      return (
        <AlertTriangle
          color={isNotPipeline ? 'orange' : 'grey'}
          height={15}
          width={20}
          text="We're unable to load the data, please try again later by refreshing the page."
        />
      )
    } else {
      return (
        <Fragment>
          <Modal isModalOpen={isSmartRateModalOpen}>
            <FadeInUpWrapper>
              <SmartRateModal
                isOpen={isSmartRateModalOpen}
                businessName={businessName}
                onClose={this.toggleModal}
                loanId={loanId}
              />
            </FadeInUpWrapper>
          </Modal>
          <TextAlign
            productUsesDefaultModel={productUsesDefaultModel}
            parentComponent={parentComponent}
            centerText={centerText}
          >
            <Container
              onClick={this.toggleModal}
              color={color}
              centerText={!productUsesDefaultModel && isNotPipeline}
              addLeftMargin={productUsesDefaultModel && isNotPipeline}
              parentComponent={parentComponent}
            >
              <span id="smart_rate">
                {smartRate}
              </span>
              &nbsp;
              {
                dataPermutation && color &&
                  <DotContainer>
                    <PermutationDots dataPermutation={dataPermutation} color={color} />
                  </DotContainer>
              }
              {
                productUsesDefaultModel && !error ?
                  (
                    <Fragment>
                      <Divider color={color} />
                      <Spacer color={color} percentVisible={showPercent} />
                      <span id="default_probability">
                        {percentText}
                      </span>
                    </Fragment>
                  )
                  : null
              }
              {
                productUsesDefaultModel && typeof smartRate === 'number' && error ? (
                  <Fragment>
                    <Divider color={color} />
                    <ErrorAlert
                      parentComponent={parentComponent}
                      color={isNotPipeline ? 'orange' : 'grey'}
                      height={15}
                      width={20}
                      text="We're having trouble fetching the R-factor right now. Please try again later."
                    />
                  </Fragment>
                ) : null
              }
            </Container>
          </TextAlign>
        </Fragment>
      )
    }
  }
}

SmartRateDisplay.propTypes = {
  businessName:            PropTypes.string,
  color:                   PropTypes.string,
  dataPermutation:         PropTypes.string,
  defaultProbability:      PropTypes.number,
  error:                   PropTypes.object,
  loanId:                  PropTypes.number,
  needsRedecision:         PropTypes.bool,
  parentComponent:         PropTypes.oneOf(['loanStatusHeader', 'pipeline', 'loansList']),
  productUsesDefaultModel: PropTypes.bool,
  smartRate:               PropTypes.number
}

export default SmartRateDisplay
